import React from "react";
import { Box, Button, DrawerBody, DrawerHeader, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@familyzone/component-library";
import { LibraryDrawerView } from ".";
import { colours } from "@/utils/themeUtils";

import emptyInbox from "@/assets/emptyInbox.svg?url";
import { FocusesList } from "./FocusesList";
import { FocusDTO } from "@/js/api/focus";
import { EditingFocus } from "./FocusPage";
import { getSignaturesForFocusResourcePicker, transformFocusTabDTOToSelectedResource } from "./FocusResourcePicker/utils";
import { useFocusStore } from "@/stores/v2/focusStore";
import { envConfig } from "@/config";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { useSignatureStore } from "@/stores/v2/signatureStore";

interface Props {
	jumpToView: (view: LibraryDrawerView) => void;
	setEditingFocus: (focus: EditingFocus) => void;
	closeDrawer: () => void;
}

export function LibraryPage(props: Props): JSX.Element {
	const { t } = useTranslation();
	const MAX_FOCUSES_IN_LIBRARY = envConfig.MAX_FOCUSES_IN_LIBRARY;

	const signatures = useSignatureStore((state) => state.signatures);
	const focusStore = useFocusStore();
	const selectedClassroomStore = useSelectedClassroomStore();

	function handleClickFocusResource(focus: FocusDTO) {
		const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(signatures);

		props.setEditingFocus({
			id: focus.id,
			name: focus.name,
			active: focus.active,
			resources: focus.focusTabs.map((resource) => transformFocusTabDTOToSelectedResource(resource, allSignatureResourceOptions)),
			isOnTheFly: !focus.name,
			studentIds: focus.studentIds || [],
		});

		props.jumpToView(LibraryDrawerView.FocusView);
	}

	return (
		<>
			<DrawerHeader bg={colours.gray[100]} p={7}>
				<Flex justifyContent={"space-between"}>
					<Text fontSize={"xl"} fontWeight={"medium"} mb={3}>
						{t("focusLibrary.libraryTitle")}
					</Text>
				</Flex>
				<Flex justifyContent={"space-between"}>
					<Text color={"neutrals.500"} mr={5} fontSize={"md"} fontWeight={400}>
						{t("focusLibrary.librarySubtitle", { max: MAX_FOCUSES_IN_LIBRARY })}
					</Text>
					<Box>
						<Tooltip
							label={t("focusLibrary.libraryMaxTooltip", { max: MAX_FOCUSES_IN_LIBRARY })}
							isDisabled={focusStore.focusLibrary.length < MAX_FOCUSES_IN_LIBRARY}
						>
							<Button
								onClick={() => props.jumpToView(LibraryDrawerView.FocusView)}
								isDisabled={focusStore.focusLibrary.length >= MAX_FOCUSES_IN_LIBRARY && !selectedClassroomStore.stableIdV1}
							>
								<ChakraFAIcon icon={"fa-arrows-to-eye"} variant="solid" mr={2} boxSize={4} />
								{t("focusLibrary.createFocus")}
							</Button>
						</Tooltip>
					</Box>
				</Flex>
			</DrawerHeader>
			<DrawerBody pt={3} pb={0} px={3}>
				{focusStore.focusLibrary.length === 0 ? (
					<Box textAlign={"center"} mt={150}>
						{focusStore.fetchingLibraryItems ? (
							<Spinner />
						) : (
							<Box>
								<Image src={emptyInbox} alt={t("focusLibrary.emptyAlt")} mx={"auto"} mb={20} />
								<Text fontSize={"sm"} color={"neutrals.300"}>
									{t("focusLibrary.emptyLine1")}
								</Text>
								<Text fontSize={"sm"} color={"neutrals.300"}>
									{t("focusLibrary.emptyLine2")}
								</Text>
							</Box>
						)}
					</Box>
				) : (
					<FocusesList handleClickFocusResource={handleClickFocusResource} hideDeleteOption={!!selectedClassroomStore.stableIdV1} />
				)}
			</DrawerBody>
		</>
	);
}
